import {showMessage} from 'exsportia-components'
import {Formik, FormikConfig} from 'formik'
import {FC, PropsWithChildren, useCallback, useMemo} from 'react'
import {useMutation, useQuery, useQueryClient} from 'react-query'
import {useDispatch} from 'react-redux'
import {useParams} from 'react-router-dom'
import {object} from 'yup'

import {User} from '../../../api/user/types'
import {UserExsportiaCardModal} from '../../../components/user-exsportia-card-modal/user-exsportia-card-modal'
import {auth} from '../../../utils/firebase'
import {useLocale} from '../../../utils/i18n'
import {useModalActions} from '../../../utils/modals'
import {CONFIRMATION_VALIDATION, EMAIL_VALIDATION, PHONE_VALIDATION, USER_NAME_VALIDATION} from '../../../utils/validation'
import {useBookingLoader} from '../../booking/hooks'
import {UserService} from '../../user'
import {useAuthState} from '../hooks'
import {AuthService} from '../index'
import {hasAccountForEmail} from '../known-account'

export type IntegratedSignUpFormType = {
  name: string
  phone: string
  email: string
  userId: string | null | undefined
  termsAndConditionsConfirm: boolean
  isRequestedToCreateAccount?: boolean
}

export const defaultUserValues: IntegratedSignUpFormType = {
  phone: '',
  email: '',
  name: '',
  userId: null,
  isRequestedToCreateAccount: true,
  termsAndConditionsConfirm: false
}

type IntegratedSignUpFormProps = {
  callback: (values: IntegratedSignUpFormType) => void // function that navigates to next step
}

const formValuesWithData = (data: User): IntegratedSignUpFormType => ({
  phone: data.phone ?? '',
  email: data.email,
  name: data.name,
  userId: null,
  isRequestedToCreateAccount: true,
  termsAndConditionsConfirm: false
})

export const IntegratedSignUpForm: FC<PropsWithChildren<IntegratedSignUpFormProps>> = ({children, callback}) => {
  const {l} = useLocale()
  const {venueId} = useParams()
  const {isLoggedIn, signIn} = useAuthState(venueId)
  const dispatch = useDispatch()
  const {setIsLoading} = useBookingLoader()
  const {openModal} = useModalActions()
  const queryClient = useQueryClient()
  const {data} = useQuery(UserService.queries.getUserQuery({cacheTime: 0, params: {id: auth.currentUser?.uid}}))
  const {mutate: createAccount} = useMutation(AuthService.mutations.integratedSignUpMutation({}))
  const initialValues = useMemo<IntegratedSignUpFormType>(() => {
    let values = defaultUserValues
    if (isLoggedIn && data) {
      values = formValuesWithData(data)
    }
    return values
  }, [data, isLoggedIn])
  const handleSubmit = useCallback<FormikConfig<IntegratedSignUpFormType>['onSubmit']>(
        (valuesDirty) => {
      const values = {
        ...valuesDirty,
        venueId,
        email: valuesDirty.email.toLowerCase(),
      }
      // check if account exist for this phone | email
      if (isLoggedIn) {
        return callback(values)
      }

      setIsLoading(true)
      // A known email signs in first, with or without "create account" (WBA-4226)
      hasAccountForEmail(queryClient, values.email).then((isKnown) => {
        if (isKnown) {
          setIsLoading(false)
          signIn(values.email)
          return
        }
        if (!values.isRequestedToCreateAccount) {
          setIsLoading(false)
          callback(values)
          return
        }
        createAccount(values, {
          onSuccess: ({password}) => {
            setIsLoading(false)
            if (password) {
              dispatch(
                AuthService.actions.signInRequestAction({
                  email: values.email,
                  password,
                  isAfterRegistration: true,
                  callback: () => {
                    openModal({
                      footerConfig: {disabled: true},
                      component: (
                        <UserExsportiaCardModal
                          name={values.name}
                          avatar={null}
                          subText={values.email}
                          withDescription={true}
                          phone={values.phone}
                        />
                      ),
                    })
                    callback(values)
                  },
                }),
              )
            }
          },
          onError: (error) => {
            setIsLoading(false)
            const responseData = error.response?.data as {code?: string; type?: string; message?: string} | undefined
            const isAccountAlreadyExist =
              error.response?.status === 302 ||
              responseData?.code === 'USER_ALREADY_EXISTS' ||
              responseData?.type === 'auth/email-already-exists' ||
              responseData?.code === 'auth/email-already-in-use' ||
              responseData?.type === 'auth/email-already-in-use' ||
              (typeof responseData?.message === 'string' && responseData.message.toLowerCase().includes('user already exists'))

            if (isAccountAlreadyExist) {
              signIn(values.email)
            } else if (responseData?.code === 'PHONE_IN_DB_UNCLAIMED') {
              showMessage(l('error_phone_unclaimed'), 'error')
            } else {
              showMessage(responseData?.message || l('something_went_wrong', {defaultValue: 'Something went wrong'}), 'error')
            }
          },
        })
      })
    },
    [venueId, isLoggedIn, signIn, callback, setIsLoading, queryClient, createAccount, dispatch, openModal, l],
  )
  const validationSchema = useMemo(
    () =>
      object().shape({
        phone: PHONE_VALIDATION,
        email: EMAIL_VALIDATION,
        name: USER_NAME_VALIDATION,
        termsAndConditionsConfirm: CONFIRMATION_VALIDATION
      }),
    [l],
  )
  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {children}
    </Formik>
  )
}
